import { Share2, SquarePlus, X } from "lucide-react";
import { useState } from "react";

import { usePwaInstallState } from "./usePwaInstallState";

// The full, user-triggered "how to install" walkthrough for iOS/iPadOS, where no programmatic
// install API exists. Opened either from the automatic guidance card's "Show me how" action or
// from the explicit nav/footer install entry points -- both share the same showIosInstructions
// flag. Deliberately separate from the automatic guidance card (PwaStatusPrompts): different
// trigger, different dismissal state, plain (not premium-gradient) styling so the two are never
// confused for one another.
//
// Instructional only. It never invokes -- or pretends to invoke -- Add to Home Screen; iOS Safari
// has no API for that, and this component does not fake one.
const STEPS: { title: string; body: string }[] = [
  {
    title: "Open the Safari Share menu",
    body: "In Safari, tap the Share button in the toolbar (the square with an upward arrow).",
  },
  {
    title: "Show more actions if needed",
    body: "Swipe up on the list of actions. If you still don't see the next step, tap Edit Actions and turn on Add to Home Screen.",
  },
  {
    title: "Select Add to Home Screen",
    body: "Tap Add to Home Screen in the list.",
  },
  {
    title: "Keep Open as Web App enabled",
    body: "Leave Open as Web App turned on so MedoPilot opens in its own window, not a Safari tab.",
  },
  {
    title: "Tap Add",
    body: "Tap Add in the top-right corner to place MedoPilot on your Home Screen.",
  },
  {
    title: "Open MedoPilot from the Home Screen",
    body: "Close Safari and launch MedoPilot from its new Home Screen icon.",
  },
];

export function IosInstallInstructions() {
  const { showIosInstructions, closeIosInstructions } = usePwaInstallState();
  const [stepIndex, setStepIndex] = useState(0);

  if (!showIosInstructions) {
    return null;
  }

  const step = STEPS[stepIndex];
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === STEPS.length - 1;

  const close = () => {
    setStepIndex(0);
    closeIosInstructions();
  };

  return (
    <div
      role="dialog"
      aria-label="Install MedoPilot on iOS"
      className="fixed inset-x-4 bottom-5 z-50 mx-auto max-w-sm rounded-2xl border border-border bg-card p-4 text-foreground shadow-soft sm:inset-x-auto sm:right-5"
    >
      <div className="flex items-start gap-3">
        <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
          {isLast ? (
            <SquarePlus className="size-4" aria-hidden="true" />
          ) : (
            <Share2 className="size-4" aria-hidden="true" />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Install MedoPilot · Step {stepIndex + 1} of {STEPS.length}
          </p>
          <p className="mt-1 text-sm font-semibold text-foreground">{step.title}</p>
          <p className="mt-1 text-sm leading-5 text-muted-foreground">{step.body}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {!isFirst ? (
              <button
                type="button"
                onClick={() => setStepIndex((index) => Math.max(0, index - 1))}
                className="inline-flex min-h-9 items-center justify-center rounded-full border border-primary/25 px-4 text-sm font-semibold text-primary transition-colors hover:bg-primary/5 focus:outline-none focus:ring-4 focus:ring-primary/20"
              >
                Back
              </button>
            ) : null}
            {!isLast ? (
              <button
                type="button"
                onClick={() => setStepIndex((index) => Math.min(STEPS.length - 1, index + 1))}
                className="inline-flex min-h-9 items-center justify-center rounded-full bg-primary px-4 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90 focus:outline-none focus:ring-4 focus:ring-primary/20"
              >
                Next
              </button>
            ) : (
              <button
                type="button"
                onClick={close}
                className="inline-flex min-h-9 items-center justify-center rounded-full bg-primary px-4 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90 focus:outline-none focus:ring-4 focus:ring-primary/20"
              >
                Done
              </button>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={close}
          aria-label="Close installation instructions"
          className="grid size-8 shrink-0 place-items-center rounded-full text-muted-foreground transition hover:bg-secondary hover:text-foreground focus:outline-none focus:ring-4 focus:ring-primary/20"
        >
          <X className="size-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
